// src/components/quizzes/Bonus10CountBy.js
//
// Count-by-10s bonus round: catch the next number in the sequence.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell from "./kit/BonusShell";

// spawn tuning
const SPAWN_MS = 420;                 // a hair faster than 500ms
const TARGET_BIAS = 0.82;            // favor nextTarget heavily
const MIN_TARGETS_ONSCREEN = 2;      // always keep at least this many nextTarget bubbles
const TARGET_SPEED_SLOWDOWN = 0.85;  // nextTarget falls ~15% slower (easier to catch)

// === Count-by config ===
const COUNT_BY = 10;     // << change this to 2,3,4,... to reuse the game
const MAX_VALUE = 200;   // highest target in the sequence

const buildSequence = (step, max) => {
  const seq = [];
  for (let v = step; v <= max; v += step) seq.push(v);
  return seq;
};

const randomDistractor = (target, max) => {
  const pool = [];
  for (let v = 1; v <= max; v++) {
    if (v !== target) pool.push(v);
  }
  // bias distractors near the target so choices feel plausible
  const nearby = pool.filter(v => Math.abs(v - target) <= Math.max(5, COUNT_BY * 2));
  const src = nearby.length >= 6 ? nearby : pool;
  return src[Math.floor(Math.random() * src.length)];
};

const SEQUENCE = buildSequence(COUNT_BY, MAX_VALUE);

const CountByBonus = (props) => (
  <BonusShell
    {...props}
    title={`Count by ${COUNT_BY}s!`}
    emoji="🔢"
    sub="The first number is done for you — catch the NEXT number, in order!"
    intro={`Count by ${COUNT_BY}. The first number is done for you, so catch the next number, then the next one. Each right number is worth five points. Watch out for bombs! Press Start when you are ready.`}
    rules={[
      { icon: "✅", text: "The next number", pts: "+5" },
      { icon: "❌", text: "Wrong or out of order", pts: "-1" },
      { icon: "🏆", text: `Reach ${MAX_VALUE} to finish!` },
    ]}
    init={(prev) => prev || { i: 1 }}
    prompt={(s) => ({
      big: `${SEQUENCE.slice(Math.max(0, s.i - 4), s.i).join(", ")}, ?`,
      small: `Count by ${COUNT_BY} — catch the next number!`,
      say: `Count by ${COUNT_BY}.`,
    })}
    spawn={(s, onScreen) => {
      const target = SEQUENCE[s.i];
      const have = onScreen.filter((x) => x.value === target).length;
      if (have < MIN_TARGETS_ONSCREEN || Math.random() < TARGET_BIAS * 0.35) return target;
      return randomDistractor(target, MAX_VALUE);
    }}
    check={(s, v) => v === SEQUENCE[s.i]}
    onRight={(s) =>
      s.i + 1 >= SEQUENCE.length
        ? { done: true, say: `You did it! You counted all the way to ${MAX_VALUE}!`, flash: "🏆 You did it!" }
        : { state: { i: s.i + 1 }, clear: true, quiet: true }
    }
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
    caughtLabel="Numbers"
  />
);

export default CountByBonus;
